/**
 * External dependencies
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import ImageUploadForm from '@Components/ImageUploadForm';
import { AspectRatio } from '@Components/AspectRatio';
import { Skeleton } from '@Components/Skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import { useAssets } from '../../hooks/useAssets';
import { useAssetTags } from '../../hooks/useAssetTags';
import { useUpdateAsset } from '../../hooks/useUpdateAsset';
import type { AssetTag, AssetTagValue } from '@Types/assets';
import { cn } from '../../utils/cn';

const TAG_CORPORATE_BOOKING = 'Corporate Booking';
const TAG_PRIVATE_DINING = 'Private Dining';

export interface ManageSpaceImagesDialogProps {
  /** Display name shown in the dialog title */
  name: string;
  /** Currently selected asset IDs */
  currentAssetIds: string[];
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should open or close */
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the selected asset IDs when the user confirms.
   * Should return a promise that resolves when the save is complete.
   */
  onSave: (assetIds: string[]) => Promise<void>;
}

const TagSelectionSchema = z.object({
  corporateBooking: z.boolean(),
  privateDining: z.boolean(),
});

const FormSchema = z.object({
  assetIds: z.array(z.string()),
  tagSelections: z.record(z.string(), TagSelectionSchema),
});

type FormValues = z.infer<typeof FormSchema>;
type TagSelection = z.infer<typeof TagSelectionSchema>;

function emptyTagSelection(): TagSelection {
  return { corporateBooking: false, privateDining: false };
}

function selectionFromTagNames(tagNames: string[]): TagSelection {
  return {
    corporateBooking: tagNames.includes(TAG_CORPORATE_BOOKING),
    privateDining: tagNames.includes(TAG_PRIVATE_DINING),
  };
}

export default function ManageSpaceImagesDialog({
  name,
  currentAssetIds,
  isOpen,
  onOpenChange,
  onSave,
}: ManageSpaceImagesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const { data: assets, status } = useAssets({
    enabled: isOpen,
  });

  const { data: tagsData, status: tagsStatus } = useAssetTags({
    enabled: isOpen,
  });

  const queryClient = useQueryClient();
  const updateAssetMutation = useUpdateAsset();

  const tagsByName = useMemo(() => {
    const map = new Map<string, AssetTag>();
    for (const t of tagsData?.data ?? []) {
      map.set(t.name, t);
    }
    return map;
  }, [tagsData]);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      assetIds: currentAssetIds,
      tagSelections: {},
    },
  });

  const rootError = form.formState.errors.root?.message;

  useEffect(() => {
    if (!isOpen || !assets) return;
    const current = form.getValues('tagSelections') ?? {};
    const next: Record<string, TagSelection> = { ...current };
    let changed = false;
    for (const asset of assets) {
      if (next[asset.id] !== undefined) continue;
      const tagNames = asset.tags?.map((t) => t.tag.name) ?? [];
      next[asset.id] = selectionFromTagNames(tagNames);
      changed = true;
    }
    if (changed) {
      form.setValue('tagSelections', next, { shouldDirty: false });
    }
  }, [assets, isOpen, form]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    form.reset({ assetIds: currentAssetIds, tagSelections: {} });
    form.clearErrors('root');
  };

  const toggleAsset = useCallback(
    (id: string) => {
      const currentIds = form.getValues('assetIds') ?? [];
      const currentSelections = form.getValues('tagSelections') ?? {};
      if (currentIds.includes(id)) {
        const nextIds = currentIds.filter((x) => x !== id);
        const { [id]: _removed, ...restSelections } = currentSelections;
        form.setValue('assetIds', nextIds, {
          shouldDirty: true,
          shouldTouch: true,
        });
        form.setValue('tagSelections', restSelections, { shouldDirty: true });
      } else {
        form.setValue('assetIds', [...currentIds, id], {
          shouldDirty: true,
          shouldTouch: true,
        });
        if (currentSelections[id] === undefined) {
          const asset = assets?.find((a) => a.id === id);
          const tagNames = asset?.tags?.map((t) => t.tag.name) ?? [];
          form.setValue(
            'tagSelections',
            {
              ...currentSelections,
              [id]: selectionFromTagNames(tagNames),
            },
            { shouldDirty: true },
          );
        }
      }
    },
    [form, assets],
  );

  const toggleTag = useCallback(
    (
      assetId: string,
      key: 'corporateBooking' | 'privateDining',
      checked: boolean,
    ) => {
      const currentSelections = form.getValues('tagSelections') ?? {};
      const prior = currentSelections[assetId] ?? emptyTagSelection();
      form.setValue(
        'tagSelections',
        {
          ...currentSelections,
          [assetId]: { ...prior, [key]: checked },
        },
        { shouldDirty: true },
      );
    },
    [form],
  );

  const isSelected = useCallback(
    (id: string, selectedIds: string[] | undefined) =>
      !!selectedIds && selectedIds.includes(id),
    [],
  );

  const handleUploadSuccess = (result?: any) => {
    queryClient.invalidateQueries({ queryKey: ['assets'] });

    const newAssets = Array.isArray(result) ? result : result?.assets;
    if (Array.isArray(newAssets) && newAssets.length > 0) {
      const currentIds = form.getValues('assetIds') ?? [];
      const currentSelections = form.getValues('tagSelections') ?? {};
      const newIds = newAssets
        .map((a: any) => a.id)
        .filter((id: string) => id && !currentIds.includes(id));
      if (newIds.length > 0) {
        form.setValue('assetIds', [...currentIds, ...newIds], {
          shouldDirty: true,
          shouldTouch: true,
        });
        const nextSelections = { ...currentSelections };
        for (const id of newIds) {
          if (nextSelections[id] === undefined) {
            nextSelections[id] = emptyTagSelection();
          }
        }
        form.setValue('tagSelections', nextSelections, { shouldDirty: true });
      }
    }
  };

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    try {
      setIsSaving(true);

      const assetList = assets ?? [];
      const updates: Array<Promise<unknown>> = [];

      for (const assetId of values.assetIds) {
        const asset = assetList.find((a) => a.id === assetId);
        if (!asset) continue;

        const selection = values.tagSelections[assetId] ?? emptyTagSelection();
        const currentTagEntries = asset.tags ?? [];
        const currentTagIds = new Set(
          currentTagEntries
            .map((t) => t.tag?.id)
            .filter((id): id is string => !!id),
        );

        const preserved: AssetTagValue[] = currentTagEntries
          .map((t) => t.tag)
          .filter(
            (tag): tag is { id: string; name: string } =>
              !!tag &&
              tag.name !== TAG_CORPORATE_BOOKING &&
              tag.name !== TAG_PRIVATE_DINING,
          )
          .map((tag) => ({ id: tag.id, name: tag.name }));

        const desired: AssetTagValue[] = [...preserved];

        if (selection.corporateBooking) {
          const existing = tagsByName.get(TAG_CORPORATE_BOOKING);
          desired.push(
            existing
              ? { id: existing.id, name: TAG_CORPORATE_BOOKING }
              : { name: TAG_CORPORATE_BOOKING },
          );
        }
        if (selection.privateDining) {
          const existing = tagsByName.get(TAG_PRIVATE_DINING);
          desired.push(
            existing
              ? { id: existing.id, name: TAG_PRIVATE_DINING }
              : { name: TAG_PRIVATE_DINING },
          );
        }

        const desiredIds = new Set(
          desired.map((t) => t.id).filter((id): id is string => !!id),
        );
        const desiredHasNewTags = desired.some((t) => !t.id);

        const idsEqual =
          desiredIds.size === currentTagIds.size &&
          Array.from(desiredIds).every((id) => currentTagIds.has(id));

        if (idsEqual && !desiredHasNewTags) continue;

        updates.push(
          updateAssetMutation.mutateAsync({
            id: assetId,
            payload: { tags: desired },
          }),
        );
      }

      if (updates.length > 0) {
        await Promise.all(updates);
      }

      await onSave(values.assetIds);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving space images:', error);
      form.setError('root', {
        type: 'server',
        message: 'We were unable to update the images. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const tagsLoading = tagsStatus === 'pending';
  const saveDisabled = isSaving || tagsLoading;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Manage images for {name}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {rootError && (
              <div className="mb-4">
                <Alert variant="destructive">
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>{rootError}</AlertDescription>
                </Alert>
              </div>
            )}

            <div>
              <div className="mb-2 text-sm font-medium text-zinc-700">
                Upload new images
              </div>
              <ImageUploadForm
                inputId="space-image-upload-input"
                onUploadSuccess={handleUploadSuccess}
              />
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-zinc-700">
                Media Library
              </div>
              <FormField
                control={form.control}
                name="assetIds"
                render={({ field }) => {
                  const selectedIds = field.value ?? [];
                  const tagSelections = form.watch('tagSelections') ?? {};
                  return (
                    <FormItem>
                      <FormControl>
                        <div className="grid max-h-128 grid-cols-3 gap-4 overflow-y-auto p-1">
                          {status === 'pending' ? (
                            <>
                              <Skeleton className="aspect-square w-full" />
                              <Skeleton className="aspect-square w-full" />
                              <Skeleton className="aspect-square w-full" />
                            </>
                          ) : !assets || assets.length === 0 ? (
                            <div className="col-span-3 flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                              <div className="font-medium text-zinc-700">
                                No uploaded images
                              </div>
                            </div>
                          ) : (
                            assets.map((asset) => {
                              const selected = isSelected(
                                asset.id,
                                selectedIds,
                              );
                              const tagNames =
                                asset.tags?.map((t) => t.tag.name) ?? [];
                              const otherTagNames = tagNames.filter(
                                (n) =>
                                  n !== TAG_CORPORATE_BOOKING &&
                                  n !== TAG_PRIVATE_DINING,
                              );
                              const sel =
                                tagSelections[asset.id] ?? emptyTagSelection();
                              const cbId = `cb-${asset.id}`;
                              const pdId = `pd-${asset.id}`;
                              return (
                                <div
                                  key={asset.id}
                                  className={cn(
                                    'relative flex w-full flex-col overflow-hidden rounded-md border bg-zinc-200 transition',
                                    selected
                                      ? 'border-primary ring-4 ring-[#CAAB6A]'
                                      : 'border-transparent hover:border-zinc-300',
                                  )}
                                >
                                  <button
                                    type="button"
                                    onClick={() => toggleAsset(asset.id)}
                                    className="relative w-full"
                                  >
                                    <AspectRatio
                                      ratio={1}
                                      className="relative w-full"
                                    >
                                      <CloudinaryImage
                                        publicId={asset.cloudinaryId}
                                        mode="original"
                                        className="h-full w-full object-contain"
                                      />
                                      {selected && (
                                        <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                          <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                        </div>
                                      )}
                                    </AspectRatio>
                                  </button>
                                  {otherTagNames.length > 0 && (
                                    <div className="flex flex-wrap gap-1 bg-white px-1.5 py-1.5">
                                      {otherTagNames.map((tagName) => (
                                        <span
                                          key={tagName}
                                          className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                                        >
                                          {tagName}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <div
                                    className={cn(
                                      'flex flex-col gap-1.5 bg-white px-2 py-2',
                                      !selected && 'opacity-50',
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        id={cbId}
                                        checked={sel.corporateBooking}
                                        disabled={!selected}
                                        onCheckedChange={(checked) =>
                                          toggleTag(
                                            asset.id,
                                            'corporateBooking',
                                            checked === true,
                                          )
                                        }
                                      />
                                      <label
                                        htmlFor={cbId}
                                        className={cn(
                                          'text-xs text-zinc-700',
                                          selected
                                            ? 'cursor-pointer'
                                            : 'cursor-not-allowed',
                                        )}
                                      >
                                        Corporate Booking
                                      </label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        id={pdId}
                                        checked={sel.privateDining}
                                        disabled={!selected}
                                        onCheckedChange={(checked) =>
                                          toggleTag(
                                            asset.id,
                                            'privateDining',
                                            checked === true,
                                          )
                                        }
                                      />
                                      <label
                                        htmlFor={pdId}
                                        className={cn(
                                          'text-xs text-zinc-700',
                                          selected
                                            ? 'cursor-pointer'
                                            : 'cursor-not-allowed',
                                        )}
                                      >
                                        Private Dining
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="link" disabled={isSaving}>
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                variant="primary"
                loading={isSaving}
                disabled={saveDisabled}
              >
                Save images
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
